import { useCallback, useEffect, useRef, useState } from 'react'
import { Knob as PrimeKnob } from 'primereact/knob'
import './Knob.css'

type KnobAccent = 'blue' | 'lightBlue' | 'orange' | 'yellow' | 'white' | 'red'

const ACCENT_COLORS: Record<KnobAccent, { range: string; value: string }> = {
  blue: { range: '#1b2a4f', value: '#60a5fa' },
  lightBlue: { range: '#1e3a5f', value: '#7dd3fc' },
  orange: { range: '#4a2a14', value: '#fdba74' },
  yellow: { range: '#4a3a12', value: '#fde047' },
  white: { range: '#2c3442', value: '#e2e8f0' },
  red: { range: '#4a1d1d', value: '#f87171' },
}

interface KnobProps {
  label: string
  value: number
  min?: number
  max?: number
  step?: number
  onChange: (value: number) => void
  disabled?: boolean
  unit?: string
  accent?: KnobAccent
  defaultValue?: number
  formatValue?: (value: number) => string
}

export function Knob({
  label,
  value,
  min = -12,
  max = 12,
  step = 0.1,
  onChange,
  disabled = false,
  unit = 'dB',
  accent = 'white',
  defaultValue,
  formatValue,
}: KnobProps) {
  const [isDragging, setIsDragging] = useState(false)
  const startY = useRef(0)
  const startValue = useRef(0)
  const colors = ACCENT_COLORS[accent] ?? ACCENT_COLORS.white

  const displayValue = formatValue
    ? formatValue(value)
    : `${value.toFixed(step < 1 ? 1 : 0)}${unit}`

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || disabled) return
      const deltaY = startY.current - e.clientY
      const range = max - min
      const sensitivity = 0.5
      const deltaValue = deltaY * sensitivity * (range / 100)
      let newValue = startValue.current + deltaValue
      newValue = Math.max(min, Math.min(max, newValue))
      if (step) {
        newValue = Math.round(newValue / step) * step
      }
      onChange(newValue)
    },
    [disabled, isDragging, max, min, onChange, step]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    document.body.style.cursor = 'default'
  }, [])

  useEffect(() => {
    if (!isDragging) return
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove, handleMouseUp, isDragging])

  return (
    <div className={`knob ${disabled ? 'knob--disabled' : ''}`}>
      <div
        className="knob__ring"
        onMouseDown={(e) => {
          if (disabled) return
          if ((e.altKey || e.metaKey) && defaultValue !== undefined) {
            e.preventDefault()
            onChange(defaultValue)
            return
          }
          e.preventDefault()
          setIsDragging(true)
          startY.current = e.clientY
          startValue.current = value
          document.body.style.cursor = 'ns-resize'
        }}
        onDoubleClick={(e) => {
          if (disabled || defaultValue === undefined) return
          e.preventDefault()
          onChange(defaultValue)
        }}
      >
        <PrimeKnob
          value={value}
          min={min}
          max={max}
          step={step}
          size={42}
          strokeWidth={11}
          valueColor={colors.value}
          rangeColor={colors.range}
          textColor="transparent"
          disabled={disabled}
          readOnly
          onChange={(e) => {
            if (disabled) return
            if (typeof e.value === 'number') {
              onChange(e.value)
            }
          }}
        />
      </div>
      <div className="knob__label">{label}</div>
      <div className="knob__value" style={{ color: colors.value }}>
        {displayValue}
      </div>
    </div>
  )
}
